import { useState } from 'react'

import { ProductBrand } from '@/app/ProductBrand'
import { toAppError } from '@/shared/errors/envelope'

/** FR-01 step 1: parent signs in with a magic link (Vietnamese UI, NFR-03). */

export interface LoginScreenProps {
  readonly onSendLink: (email: string) => Promise<void>
}

type State = 'idle' | 'sending' | 'sent' | 'error' | 'rate_limited' | 'not_invited'

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function LoginScreen({ onSendLink }: LoginScreenProps): React.JSX.Element {
  const [email, setEmail] = useState('')
  const [state, setState] = useState<State>('idle')
  const valid = EMAIL.test(email.trim())

  const submit = async (event: React.SyntheticEvent) => {
    event.preventDefault()
    if (!valid) return
    setState('sending')
    try {
      await onSendLink(email.trim())
      setState('sent')
    } catch (e) {
      const code = toAppError(e).code
      setState(
        code === 'RATE_LIMITED'
          ? 'rate_limited'
          : code === 'ACCESS_DENIED'
            ? 'not_invited'
            : 'error',
      )
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-6 px-6 py-12">
      <div className="text-center">
        <p className="text-5xl" aria-hidden>
          📘
        </p>
        <ProductBrand asHeading tagline className="mt-3" />
        <p className="mt-3 text-slate-600 dark:text-slate-400">
          Đăng nhập dành cho phụ huynh
        </p>
      </div>

      {state === 'sent' ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-center dark:border-emerald-900 dark:bg-emerald-950/40">
          <p className="text-3xl" aria-hidden>
            ✉️
          </p>
          <p className="mt-2 font-semibold">Kiểm tra email của bạn</p>
          <p className="mt-1 text-slate-600 dark:text-slate-400">
            Mở liên kết đăng nhập đã gửi tới <strong>{email.trim()}</strong> trên thiết bị
            này.
          </p>
          <button
            type="button"
            className="mt-4 text-sky-700 underline dark:text-sky-400"
            onClick={() => {
              setState('idle')
            }}
          >
            Dùng email khác
          </button>
        </div>
      ) : (
        <form className="flex flex-col gap-3" onSubmit={(e) => void submit(e)} noValidate>
          <label className="flex flex-col gap-1">
            <span className="font-medium">Email</span>
            <input
              type="email"
              autoComplete="email"
              inputMode="email"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
              }}
              className="rounded-xl border border-slate-300 px-4 text-lg dark:border-slate-700 dark:bg-slate-900"
              placeholder="ten@example.com"
            />
          </label>
          <button
            type="submit"
            disabled={!valid || state === 'sending'}
            className="h-12 rounded-xl bg-sky-700 text-lg font-semibold text-white disabled:opacity-50"
          >
            {state === 'sending' ? 'Đang gửi…' : 'Gửi liên kết đăng nhập'}
          </button>
          {state === 'error' && (
            <p role="alert" className="text-rose-700 dark:text-rose-400">
              Không gửi được liên kết. Vui lòng thử lại.
            </p>
          )}
          {state === 'not_invited' && (
            <p role="alert" className="text-rose-700 dark:text-rose-400">
              Email này chưa được mời dùng ứng dụng. Ứng dụng đang thử nghiệm và chỉ dành
              cho gia đình được mời. Hãy kiểm tra lại email hoặc liên hệ người đã mời bạn.
            </p>
          )}
          {state === 'rate_limited' && (
            <p role="alert" className="text-rose-700 dark:text-rose-400">
              Bạn đã yêu cầu quá nhiều lần. Vui lòng đợi một lát rồi thử lại.
            </p>
          )}
        </form>
      )}
    </main>
  )
}
